import React from 'react';
import styled from 'styled-components';
// Icons
import Hour16 from '../icons/Hour16';
import { iconUrl } from '../rapport/rapportIcons';
import { AUTODIAG, TITLE_FONT } from './autodiagChoiceTokens';

// Carte de l'écran de choix autodiag (FIX-430) — assistant LLM vs formulaire
// Tally. Présentationnelle pure : aucune logique métier, aucun tracking.
//
// Vrai <button type="button"> en racine : accessibilité clavier native
// (Tab + Entrée/Espace), carte cliquable en entier, UN SEUL focusable.
// NOTE : volontairement un styled.button et non styled(Card) as="button" —
// l'atome Card impose overflow="hidden" (rognerait le badge « Nouveau »,
// positionné à cheval sur le bord haut) et la prop `as` court-circuiterait
// la chaîne Card→Box.
// AUCUN overflow:hidden ici ni dans les enfants — le badge déborde de 14px
// au-dessus du bord de la carte, c'est voulu.
const CardButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: ${AUTODIAG.dark};
  background-color: ${(p) =>
    p.$variant === 'primary' ? AUTODIAG.greenPastel : AUTODIAG.white};
  /* Bordure transparente sur la carte pastel : égalise les métriques
     border-box face à la carte bordée (les deux pieds restent alignés). */
  border: 1px solid
    ${(p) => (p.$variant === 'primary' ? 'transparent' : AUTODIAG.border)};
  border-radius: ${AUTODIAG.radiusCard};
  padding: ${AUTODIAG.cardPadTopMobile}px 20px 20px;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  ${(p) => p.theme.mediaQueries.tablet} {
    padding: ${AUTODIAG.cardPadTopDesktop}px 24px 24px;
  }
  &:hover {
    box-shadow: 0 4px 16px rgba(49, 73, 89, 0.12);
    transform: translateY(-2px);
  }
  &:focus-visible {
    /* Anneau plein et contrasté (≥3:1 sur pastel comme sur blanc) — le
       rgba(89,95,231,.4) utilisé ailleurs est trop clair sur #e9ffcd. */
    outline: 3px solid ${AUTODIAG.linkBlue};
    outline-offset: 2px;
  }
  /* Reduced motion : on coupe transition et translation (du mouvement),
     mais on GARDE l'ombre au hover — une ombre statique n'est pas du
     mouvement, et c'est la seule affordance hover de la carte. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

// Badge « Nouveau » : absolu, à cheval sur le bord haut de la carte —
// 36px de haut, déborde de 14px au-dessus, descend 22px dans la carte.
// Le padding-top de la carte (36/32px) et le margin-top de la grille
// (32/30px) sont calés sur ces valeurs (autodiagChoiceTokens.js).
// aria-hidden : « Nouveau » est déjà annoncé via l'aria-label de la carte.
// L'atome Badge n'est pas réutilisable ici (margin/line-height/padding fixes
// et mapping couleur du thème incompatibles avec la géométrie absolue).
const BadgeNew = styled.span`
  position: absolute;
  top: -${AUTODIAG.badgeOverhang}px;
  /* Aligné sur le padding horizontal de la carte (20px mobile, 24px ≥768). */
  left: 20px;
  ${(p) => p.theme.mediaQueries.tablet} {
    left: 24px;
  }
  display: inline-flex;
  align-items: center;
  height: ${AUTODIAG.badgeHeight}px;
  padding: 0 14px;
  border-radius: 999px;
  background: ${AUTODIAG.dark};
  color: ${AUTODIAG.white};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: ${AUTODIAG.bodyTracking};
  white-space: nowrap;
`;

// Icône brand + titre SUR LA MÊME LIGNE (gain de pli mesuré : 43px).
// Sizing d'icône scopé à CardHead > img — jamais de règle `.card img`.
const CardHead = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  & > img {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
`;

const CardTitle = styled.span`
  font-family: ${TITLE_FONT};
  font-size: 20px;
  /* 900 et non 700 : la seule face AronVF déclarée est la Black (900) —
     déclarer 700 rendrait quand même la Black (weight-matching CSS) en
     mentant sur la valeur. Même usage que les pages rapport. */
  font-weight: 900;
  letter-spacing: ${AUTODIAG.titleTracking};
  line-height: 1.2;
`;

// min-height : aligne les blocs titre+chapô des deux cartes côte à côte
// (les descriptions n'ont pas la même longueur).
const CardDesc = styled.span`
  display: block;
  min-height: 42px;
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: ${AUTODIAG.bodyTracking};
`;

const CardPreview = styled.span`
  display: block;
  margin-top: 10px;
`;

const CardMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;
  letter-spacing: ${AUTODIAG.bodyTracking};
`;

// Pied de carte : min-height fixe + margin-top auto — les deux pieds
// restent alignés en bas de carte quels que soient les libellés et la
// hauteur des contenus (les cartes s'étirent via align-items: stretch).
const CardAct = styled.span`
  display: flex;
  align-items: center;
  min-height: ${AUTODIAG.cardActMinHeight}px;
  margin-top: auto;
  padding-top: 8px;
`;

// CTA VISUELS — des <span> stylés, PAS des <button>/<a>. C'est délibéré et
// ça doit le rester : la carte entière est le bouton, et le critère
// d'acceptation impose EXACTEMENT UN élément focusable par carte. En faire
// de vrais boutons créerait un bouton imbriqué dans un bouton (HTML invalide)
// et un deuxième arrêt de Tab. Ne pas « corriger ».
const CtaButtonVisual = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 13px 24px;
  background: ${AUTODIAG.dark};
  color: ${AUTODIAG.white};
  border-radius: ${AUTODIAG.radiusButton};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: ${AUTODIAG.bodyTracking};
`;

const CtaLinkVisual = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${AUTODIAG.linkBlue};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: ${AUTODIAG.bodyTracking};
  text-decoration: underline;
  text-underline-offset: 2px;
`;

/**
 * Carte de l'écran de choix autodiag (assistant LLM vs formulaire Tally).
 * @param {string} variant 'primary' (pastel + bouton dark) | 'secondary' (blanc + lien bleu)
 * @param {string} iconName nom d'icône brand (static/img/brand/{name}.svg)
 * @param {string} title titre de la carte — porte la MÉTHODE
 * @param {string} description description courte
 * @param {string} duration coût d'entrée ("Environ 1 minute")
 * @param {string} badge libellé de badge optionnel ("Nouveau")
 * @param {node} preview aperçu du parcours (décoratif)
 * @param {string} ctaLabel libellé du CTA visuel — porte le RÉSULTAT, identique des deux côtés
 * @param {string} ariaLabel libellé annoncé UNIQUE (méthode + résultat + durée)
 * @param {function} onSelect handler de sélection
 * @param {string} testId data-testid pour les tests
 */
export default function AutodiagFlowCard({
  variant = 'secondary',
  iconName,
  title,
  description,
  duration,
  badge,
  preview,
  ctaLabel,
  ariaLabel,
  onSelect,
  testId,
}) {
  return (
    <CardButton
      type="button"
      onClick={onSelect}
      $variant={variant}
      data-testid={testId}
      aria-label={ariaLabel}>
      {badge ? (
        <BadgeNew aria-hidden="true" data-testid={`${testId}-badge`}>
          {badge}
        </BadgeNew>
      ) : null}
      <CardHead>
        <img src={iconUrl(iconName)} alt="" />
        <CardTitle>{title}</CardTitle>
      </CardHead>
      <CardDesc>{description}</CardDesc>
      <CardPreview aria-hidden="true">{preview}</CardPreview>
      <CardMeta>
        <Hour16 color={AUTODIAG.dark} />
        <span>{duration}</span>
      </CardMeta>
      <CardAct data-testid={`${testId}-act`}>
        {variant === 'primary' ? (
          <CtaButtonVisual>{ctaLabel}</CtaButtonVisual>
        ) : (
          <CtaLinkVisual>{ctaLabel}</CtaLinkVisual>
        )}
      </CardAct>
    </CardButton>
  );
}
